import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import axios from "axios";

// Иконка для закрытия уведомлений
import { ReactComponent as LoginCross } from "../../assets/icons/landing/login-cross.svg";
import "./Landing.css";

// ─── DATA ────────────────────────────────────────────────────
const TRADE = "/market?symbol=BTCUSDT";

// Только перечень инструментов: цена, изменение за 24ч и оборот приходят из живого потока
const ASSETS = [
  ["BTC", "Bitcoin"], ["ETH", "Ethereum"], ["SOL", "Solana"], ["BNB", "BNB"], ["XRP", "XRP"],
  ["ADA", "Cardano"], ["AVAX", "Avalanche"], ["ARB", "Arbitrum"], ["DOGE", "Dogecoin"], ["TRX", "TRON"],
].map(([sym, name]) => ({ sym, name, price: null, chg: null, qv: null }));

const NAV = [["platform", "Платформа"], ["features", "Возможности"], ["markets", "Рынки"], ["security", "Безопасность"]];
const RAIL = [...NAV, ["start", "Начало работы"]];
const FILTERS = [["all", "Все"], ["up", "Растут"], ["down", "Падают"], ["vol", "По обороту"]];
const CALC_BASES = ["BTC", "ETH", "SOL", "XRP"];

const TOOLS = [
  { n: "Рынки", d: "Список торговых пар с текущей ценой, изменением за 24 часа и оборотом. Данные обновляются в реальном времени.", s: [["цена", "онлайн"], ["изменение", "за 24 часа"], ["оборот", "за 24 часа"]] },
  { n: "Торговля", d: "Страница торговли для выбранной пары. Открыть её можно в один клик из ленты котировок или из таблицы рынков.", s: [["открытие", "в один клик"], ["котировка", "в USDT"], ["данные", "онлайн"]] },
  { n: "Активы", d: "Раздел личного кабинета с балансом и средствами на вашем счёте.", s: [["раздел", "Активы"], ["доступ", "в кабинете"], ["данные", "по аккаунту"]] },
  { n: "Портфель", d: "Раздел личного кабинета для обзора вашего портфеля.", s: [["раздел", "Портфель"], ["доступ", "в кабинете"], ["данные", "по аккаунту"]] },
];

const GUARDS = [
  ["Пароль", "Используйте длинный уникальный пароль и не повторяйте его на других сервисах."],
  ["Письма и ссылки", "Не переходите по подозрительным ссылкам и никому не сообщайте пароль и коды подтверждения."],
  ["Адрес сайта", "Перед вводом данных проверяйте адрес в строке браузера и наличие защищённого соединения."],
];

const STEPS = [
  ["01", "Регистрация", "Укажите электронную почту и придумайте пароль — так создаётся аккаунт."],
  ["02", "Пополнение", "Пополните баланс в личном кабинете, чтобы начать торговать."],
  ["03", "Первая сделка", "Выберите пару в списке рынков, изучите котировки и переходите к торговле."],
];

const DEPTH_POINTS = [
  ["Живые котировки.", "Цена, изменение за сутки и оборот обновляются прямо на странице, без перезагрузки."],
  ["Быстрый переход к торговле.", "Из ленты котировок или таблицы рынков можно сразу открыть нужную пару."],
  ["Единый кабинет.", "Активы и портфель доступны в личном кабинете рядом с рынками."],
  ["Понятный старт.", "Чтобы начать, достаточно зарегистрироваться по электронной почте."],
];

// ─── UTILS ───────────────────────────────────────────────────
// Пока живых данных нет, вместо цифр показываем прочерк — ничего не выдумываем
const fmt = (v) => {
  if (v == null) return "—";
  const d = v >= 1000 ? 2 : v >= 1 ? 3 : 5;
  return v.toLocaleString("ru-RU", { minimumFractionDigits: d, maximumFractionDigits: d });
};
const usd = (v) => (v == null ? "—" : "$" + fmt(v));
const pct = (c) => (c == null ? "—" : (c >= 0 ? "+" : "") + c.toFixed(2) + "%");
const dirOf = (c) => (c == null ? "" : c >= 0 ? "up" : "down");
const fmtVol = (q) =>
  !q ? "—" : q >= 1e9 ? "$" + (q / 1e9).toFixed(2) + "B" : q >= 1e6 ? "$" + (q / 1e6).toFixed(1) + "M" : "$" + Math.round(q).toLocaleString("en-US");

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};
const go = (id) => (e) => { e.preventDefault(); scrollToId(id); };

const sparkPath = (pts, w = 88, h = 26) => {
  const mn = Math.min(...pts), rg = Math.max(...pts) - mn || 1;
  return pts.map((p, i) => `${i ? "L" : "M"}${((i / (pts.length - 1)) * w).toFixed(1)} ${(h - 2 - ((p - mn) / rg) * (h - 4)).toFixed(1)}`).join(" ");
};

// Иллюстрация глубины рынка (декоративная, не реальный стакан)
const genDepth = () => {
  let cb = 0, ca = 0;
  const bids = [], asks = [];
  for (let i = 0; i < 28; i++) {
    cb += 40 + Math.random() * 90 + i * 7;
    ca += 38 + Math.random() * 90 + i * 7;
    bids.push(cb); asks.push(ca);
  }
  return { bids, asks, cb, ca };
};

const notify = (msg, variant, className) =>
  enqueueSnackbar(msg, {
    variant,
    className,
    action: (key) => (
      <button className="close-button" onClick={() => closeSnackbar(key)} style={{ background: "none", border: "none", cursor: "pointer", color: "#fff" }}>
        <LoginCross />
      </button>
    ),
  });

// ─── HOOKS ───────────────────────────────────────────────────
// Реальные данные Binance (WebSocket). Одно подключение на всю страницу, с переподключением.
const WS_URL = "wss://stream.binance.com:9443/ws/!miniTicker@arr";

function useBinanceLive(initial) {
  const [data, setData] = useState(initial);
  const [flashes, setFlashes] = useState({});
  const [live, setLive] = useState(false);

  useEffect(() => {
    let latest = initial, ws, stopped = false;
    const timers = [];

    const connect = () => {
      ws = new WebSocket(WS_URL);
      ws.onopen = () => setLive(true);
      ws.onerror = () => { };
      ws.onclose = () => {
        if (stopped) return;
        setLive(false);
        timers.push(setTimeout(connect, 4000));
      };
      ws.onmessage = (event) => {
        let stream;
        try { stream = JSON.parse(event.data); } catch (e) { return; }
        const fl = {};
        const next = latest.map((a) => {
          const t = stream.find((x) => x.s === a.sym + "USDT");
          if (!t) return a;
          const price = parseFloat(t.c), open = parseFloat(t.o);
          if (!price || price === a.price) return a;
          if (a.price != null) fl[a.sym] = price > a.price ? "up" : "down";
          return { ...a, price, chg: ((price - open) / open) * 100, qv: parseFloat(t.q) || a.qv };
        });
        if (next.every((a, i) => a === latest[i])) return;
        latest = next;
        setData(next);
        setLive(true);
        if (Object.keys(fl).length) {
          setFlashes((f) => ({ ...f, ...fl }));
          timers.push(setTimeout(() => setFlashes((f) => {
            const n = { ...f };
            Object.keys(fl).forEach((k) => { n[k] = null; });
            return n;
          }), 600));
        }
      };
    };
    connect();

    return () => { stopped = true; ws.close(); timers.forEach(clearTimeout); };
  }, []);

  return [data, flashes, live];
}

// Реальная история цен за 7 дней (закрытия 6-часовых свечей) для мини-графиков
function useSparks() {
  const [sparks, setSparks] = useState({});
  useEffect(() => {
    let dead = false;
    ASSETS.forEach((a) => {
      fetch(`https://api-ru.fynex.cc/charts/api/klines?symbol=${a.sym}USDT&interval=6h&limit=28`)
        .then((r) => r.json())
        .then((k) => { if (!dead && Array.isArray(k)) setSparks((p) => ({ ...p, [a.sym]: k.map((x) => parseFloat(x[4])) })); })
        .catch(() => { });
    });
    return () => { dead = true; };
  }, []);
  return sparks;
}

function useReveal() {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) { setSeen(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } }, { threshold: 0.08 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, seen];
}

// ─── SMALL COMPONENTS ────────────────────────────────────────
const Logo = () => (
  <span className="logo"><span className="logo-mark" /><span className="logo-mark" /><b>FYNEX</b></span>
);

function Loader({ onDone }) {
  const [p, setP] = useState(0);
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    let v = 0;
    const timers = [];
    const tick = setInterval(() => {
      v += Math.random() * 18 + 4;
      if (v >= 100) {
        v = 100;
        clearInterval(tick);
        setPhase(1);
        timers.push(setTimeout(() => setPhase(2), 700), setTimeout(onDone, 1200));
      }
      setP(Math.min(100, Math.round(v)));
    }, 80);
    return () => { clearInterval(tick); timers.forEach(clearTimeout); };
  }, [onDone]);

  return (
    <div className={"loader" + (phase === 2 ? " out" : "")}>
      <Logo />
      <div className="loader-bar"><i style={{ width: p + "%" }} /></div>
      <div className="loader-pct">{phase === 1 ? "ГОТОВО" : `ЗАГРУЗКА ${p}%`}</div>
    </div>
  );
}

function Clock() {
  const now = () => new Date().toISOString().substr(11, 8) + " UTC";
  const [s, setS] = useState(now);
  useEffect(() => { const i = setInterval(() => setS(now()), 1000); return () => clearInterval(i); }, []);
  return <span>{s}</span>;
}

function Band({ id, kicker, children }) {
  const [ref, seen] = useReveal();
  return (
    <section ref={ref} id={id} className={"band rv" + (seen ? " in" : "")}>
      <div className="inner">
        {kicker && <div className="kicker">{kicker}</div>}
        {children}
      </div>
    </section>
  );
}

// ─── CALCULATOR ──────────────────────────────────────────────
function Calc({ assets, isLoggedIn, navigate }) {
  const [side, setSide] = useState("buy");
  const [amount, setAmount] = useState("1000");
  const [base, setBase] = useState("BTC");
  const [quote, setQuote] = useState("USDT");

  const a = assets.find((x) => x.sym === base) || assets[0];
  const v = parseFloat(amount) || 0;
  const buy = side === "buy";
  const result = a.price ? (buy ? v / a.price : v * a.price) : null;

  const baseSel = (
    <select value={base} onChange={(e) => setBase(e.target.value)} aria-label="Актив">
      {CALC_BASES.map((s) => <option key={s}>{s}</option>)}
    </select>
  );
  const quoteSel = (
    <select value={quote} onChange={(e) => setQuote(e.target.value)} aria-label="Валюта">
      {["USDT", "USDC"].map((s) => <option key={s}>{s}</option>)}
    </select>
  );

  return (
    <div className="calc">
      <div className="calc-tabs">
        <button className={buy ? "on" : ""} onClick={() => setSide("buy")}>Купить</button>
        <button className={!buy ? "on" : ""} onClick={() => setSide("sell")}>Продать</button>
      </div>
      <div className="calc-body">
        <div className="field">
          <label>{buy ? "Вы платите" : "Вы продаёте"}</label>
          <div className="row">
            <input type="number" value={amount} min="0" step="10" inputMode="decimal" aria-label="Сумма" onChange={(e) => setAmount(e.target.value)} />
            {buy ? quoteSel : baseSel}
          </div>
        </div>
        <div className="swap">
          <button aria-label="Поменять местами" onClick={() => setSide(buy ? "sell" : "buy")}>
            <svg viewBox="0 0 16 16"><path d="M4 2v12M4 14 1.5 11.5M4 2 6.5 4.5M12 14V2M12 2l2.5 2.5M12 14l-2.5-2.5" /></svg>
          </button>
        </div>
        <div className="field">
          <label>Вы получаете</label>
          <div className="row">
            <span className="out">{result == null ? "—" : result.toLocaleString("ru-RU", { maximumFractionDigits: buy ? 6 : 2 })}</span>
            {buy ? baseSel : quoteSel}
          </div>
        </div>
        <div className="calc-meta">
          <div><span>Курс</span><b>1 {a.sym} = {usd(a.price)}</b></div>
          <div><span>Изменение за 24 часа</span><b className={dirOf(a.chg)}>{pct(a.chg)}</b></div>
          <div><span>Расчёт</span><b>ориентировочный</b></div>
        </div>
        <button className="btn btn-primary" onClick={() => navigate(isLoggedIn ? `/market?symbol=${a.sym}USDT` : "/SignUp")}>
          {isLoggedIn ? "Перейти к торгам" : "Открыть счёт"}
        </button>
      </div>
    </div>
  );
}

// ─── PLATFORM (визуал + живые данные BTC) ────────────────────
function Depth({ btc }) {
  const [d, setD] = useState(genDepth);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const t = setInterval(() => setD(genDepth()), 2800);
    return () => clearInterval(t);
  }, []);

  const W = 640, H = 230, max = Math.max(d.cb, d.ca);
  const y = (v) => H - 12 - (v / max) * (H - 38);
  const path = (arr, dir) => {
    const half = W / 2, step = half / arr.length, endX = dir < 0 ? 0 : W;
    return arr.map((v, i) => `${i ? "L" : "M"}${(dir < 0 ? half - i * step : half + i * step).toFixed(1)} ${y(v).toFixed(1)}`).join("")
      + `L${endX} ${y(arr[arr.length - 1]).toFixed(1)}L${endX} ${H}L${half} ${H}Z`;
  };

  return (
    <div className="depth-grid">
      <div className="depth-panel">
        <div className="dp-head">
          <span className="pair">BTC / USDT</span>
          <span className="mono mute">глубина рынка</span>
          <span className="spread"></span>
        </div>
        <div className="depth-chart">
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
            <defs>
              <linearGradient id="fxGb" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#10C97F" stopOpacity=".34" /><stop offset="1" stopColor="#10C97F" stopOpacity=".02" /></linearGradient>
              <linearGradient id="fxGa" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#E8574A" stopOpacity=".30" /><stop offset="1" stopColor="#E8574A" stopOpacity=".02" /></linearGradient>
            </defs>
            <path d={path(d.bids, -1)} fill="url(#fxGb)" stroke="#10C97F" strokeWidth="1.3" />
            <path d={path(d.asks, 1)} fill="url(#fxGa)" stroke="#E8574A" strokeWidth="1.3" />
            <line x1="320" x2="320" y1="6" y2={H} stroke="rgba(160,205,190,.28)" strokeDasharray="3 4" />
            {btc.price != null && <text x="320" y="16" fill="#9AAAA3" fontFamily="IBM Plex Mono" fontSize="11" textAnchor="middle">{Math.round(btc.price).toLocaleString("ru-RU")}</text>}
          </svg>
        </div>
        <div className="dp-foot">
          <div><span>последняя</span><b className="mono">{usd(btc.price)}</b></div>
          <div><span>изменение 24ч</span><b className={"mono " + dirOf(btc.chg)}>{pct(btc.chg)}</b></div>
          <div><span>оборот 24ч</span><b className="mono">{fmtVol(btc.qv)}</b></div>
        </div>
      </div>
      <div className="depth-note">
        <h2>Следите за рынком в реальном времени</h2>
        <ul>
          {DEPTH_POINTS.map(([b, t]) => (
            <li key={b}><span className="bullet" /><span><b>{b}</b> {t}</span></li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ─── TOOLS ───────────────────────────────────────────────────
function Tools() {
  const [i, setI] = useState(0);
  const t = TOOLS[i];
  return (
    <div className="tools">
      <div className="tool-list">
        {TOOLS.map((x, k) => (
          <button key={x.n} className={k === i ? "on" : ""} onClick={() => setI(k)}>
            <span className="idx">0{k + 1}</span><span className="nm">{x.n}</span>
          </button>
        ))}
      </div>
      <div className="tool-panel">
        <div className="fade" key={i}>
          <h3>{t.n}</h3>
          <p>{t.d}</p>
          <div className="tool-specs">
            {t.s.map(([l, v]) => <div key={l}><span>{l}</span><b>{v}</b></div>)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MARKETS TABLE ───────────────────────────────────────────
function Markets({ assets, flashes, navigate }) {
  const [f, setF] = useState("all");
  const sparks = useSparks();

  let list = assets;
  if (f === "up") list = assets.filter((a) => a.chg > 0).sort((x, y) => y.chg - x.chg);
  if (f === "down") list = assets.filter((a) => a.chg < 0).sort((x, y) => x.chg - y.chg);
  if (f === "vol") list = [...assets].sort((x, y) => (y.qv || 0) - (x.qv || 0));

  return (
    <>
      <div className="chips">
        {FILTERS.map(([k, l]) => (
          <button key={k} className={"chip" + (f === k ? " on" : "")} onClick={() => setF(k)}>{l}</button>
        ))}
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Инструмент</th><th>Последняя</th><th>24ч</th>
            <th className="col-hide">Оборот 24ч</th><th className="col-hide">7 дней</th><th />
          </tr>
        </thead>
        <tbody>
          {list.length === 0 && <tr><td colSpan={6} className="mute" style={{ textAlign: "center" }}>Нет данных</td></tr>}
          {list.map((a) => {
            const pts = sparks[a.sym];
            const rising = pts && pts[pts.length - 1] >= pts[0];
            return (
              <tr key={a.sym} className={flashes[a.sym] ? "flash-" + flashes[a.sym] : ""}>
                <td>
                  <div className="asset">
                    <span className="tick"><img src={`https://api-ru.fynex.cc/api/static/icons/${a.sym.toLowerCase()}.png`} style={{ width: '100%' }} alt="" /></span>
                    <span><b>{a.name}</b><span>{a.sym}/USDT</span></span>
                  </div>
                </td>
                <td>{fmt(a.price)}</td>
                <td className={dirOf(a.chg)}>{pct(a.chg)}</td>
                <td className="col-hide">{fmtVol(a.qv)}</td>
                <td className="col-hide">
                  {pts ? <svg className="spark" viewBox="0 0 88 26"><path d={sparkPath(pts)} style={{ stroke: rising ? "var(--green)" : "var(--red)" }} /></svg> : <span className="mute">—</span>}
                </td>
                <td><button className="btn btn-line btn-sm" onClick={() => navigate(`/market?symbol=${a.sym}USDT`)}>Торговать</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

// ─── MAIN ────────────────────────────────────────────────────
const Landing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [shown, setShown] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [active, setActive] = useState("");
  const [assets, flashes, live] = useBinanceLive(ASSETS);

  // Auth logic & Form state
  // eslint-disable-next-line no-unused-vars
  const [email, setEmail] = useState("");
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";

  const handleLoaded = useCallback(() => {
    setLoading(false);
    setTimeout(() => setShown(true), 50);
  }, []);

  // Подсветка текущего раздела в левом рельсе
  useEffect(() => {
    const io = new IntersectionObserver(
      (es) => es.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-45% 0px -50% 0px" }
    );
    RAIL.forEach(([id]) => { const el = document.getElementById(id); if (el) io.observe(el); });
    return () => io.disconnect();
  }, []);

  // Блокируем прокрутку страницы под мобильным меню
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // Quick Registration Handler (форма на лендинге скрыта, как и раньше — обработчик сохранён)
  // eslint-disable-next-line no-unused-vars
  const registerSubmit = async (e) => {
    e.preventDefault();

    if (!/^\S+@\S+\.\S+$/.test(email)) {
      notify(t("landing.auth.errRegMsg") || "Неверный формат email", "error");
      return null;
    }

    const time = new Date().getTime();
    axios
      .post(`https://api-ru.fynex.cc/api/api/users`, {
        email: email,
        password: "password",
        confirmPassword: "password",
        phone_number: `${time}`,
        username: `username${time}`,
        name: "name",
        surname: "surname",
        country: "country",
        city: "city",
      })
      .then((res) => {
        notify(t("personalInfo.success") || "Успешная регистрация!", "success", "snacksuccess");
        localStorage.setItem("username", `username${time}`);
        localStorage.setItem("userEmail", email);
        localStorage.setItem("userName", "name");
        localStorage.setItem("userSurname", "surname");
        localStorage.setItem("token", res.data);
        localStorage.setItem("isLoggedIn", true);
        navigate(`/market?symbol=BTCUSDT`, { replace: true });
        window.location.reload();
      })
      .catch((error) => {
        notify(`${t("landing.err") || "Ошибка"}: ${error?.response?.data?.detail || "Попробуйте позже"}`, "error", "snackerror");
      });
  };

  const to = (path) => (e) => { e.preventDefault(); navigate(path); };
  const goHome = (e) => { e.preventDefault(); navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const appLinks = [[t("header.trade"), TRADE], [t("header.assets"), "/account/"], [t("header.portfolio"), "/account/portfolio"]];
  const menuGo = (fn) => (e) => { e.preventDefault(); setMobileOpen(false); setTimeout(fn, 60); };

  const btc = assets[0];
  const FOOT = [
    ["Торговля", [{ l: "Рынки", id: "markets" }, { l: "Торговля", p: TRADE }]],
    ["Компания", [{ l: "О бирже", p: "/policy-about" }, { l: "Контакты", p: "/policy-about" }]],
    ["Поддержка", [{ l: "Условия и политика", p: "/policy" }, { l: "Безопасность", id: "security" }]],
  ];

  return (
    <div className="fx">
      {loading && <Loader onDone={handleLoaded} />}

      {/* ── MOBILE MENU ── */}
      <div className={"mmenu" + (mobileOpen ? " open" : "")}>
        <div className="mmenu-top">
          <Logo />
          <button className="btn btn-line btn-sm" onClick={() => setMobileOpen(false)}>Закрыть ✕</button>
        </div>
        <nav className="mmenu-nav">
          {(isLoggedIn ? appLinks.map(([l, p]) => ({ l, fn: () => navigate(p), href: p })) : NAV.map(([id, l]) => ({ l, fn: () => scrollToId(id), href: "#" + id })))
            .map((x, i) => <a key={x.l} data-n={"0" + (i + 1)} href={x.href} onClick={menuGo(x.fn)}>{x.l}</a>)}
        </nav>
        <div className="mmenu-acts">
          {!isLoggedIn ? (
            <>
              <button className="btn btn-line" onClick={() => { setMobileOpen(false); navigate("/Login"); }}>Войти</button>
              <button className="btn btn-primary" onClick={() => { setMobileOpen(false); navigate("/SignUp"); }}>Открыть счёт</button>
            </>
          ) : (
            <button className="btn btn-primary" onClick={() => { setMobileOpen(false); navigate(TRADE); }}>Перейти к торгам</button>
          )}
        </div>
      </div>

      <div className={"stage" + (shown ? " shown" : "")}>
        {/* ── SYSBAR ── */}
        {/* <div className="sysbar">
          <span className="ok"><span className={"led" + (live ? "" : " off")} />{live ? "Котировки в реальном времени" : "Подключение к потоку котировок…"}</span>
          <span className="right"><Clock /><span>Рынок: 24/7</span><span>RU</span></span>
        </div> */}

        {/* ── MASTHEAD ── */}
        <header className="masthead">
          <a className="logo" href="/" onClick={goHome}><span className="logo-mark" /><span className="logo-mark" /><b>FYNEX</b></a>
          <nav className="mainnav">
            {isLoggedIn
              ? appLinks.map(([l, p]) => <a key={p} href={p} onClick={to(p)}>{l}</a>)
              : NAV.map(([id, l]) => <a key={id} href={"#" + id} onClick={go(id)}>{l}</a>)}
          </nav>
          <span className="spacer" />
          <div className="acts">
            {!isLoggedIn ? (
              <>
                <button className="btn btn-line btn-sm" onClick={() => navigate("/Login")}>Войти</button>
                <button className="btn btn-primary btn-sm" onClick={() => navigate("/SignUp")}>Открыть счёт</button>
              </>
            ) : (
              <button className="btn btn-primary btn-sm" onClick={() => navigate(TRADE)}>Торговать</button>
            )}
            <button className="burger" aria-label="Меню" onClick={() => setMobileOpen(true)}><span /><span /><span /></button>
          </div>
        </header>

        <div className="shell" id="top">
          {/* ── RAIL ── */}
          <aside className="rail">
            <h4>Разделы</h4>
            <ol>
              {RAIL.map(([id, l]) => (
                <li key={id}><a href={"#" + id} className={active === id ? "on" : ""} onClick={go(id)}>{l}</a></li>
              ))}
            </ol>
            <div className="rail-foot">
              BTC / USDT
              <b className="mono">{usd(btc.price)}</b>
              24ч: <span className={dirOf(btc.chg)}>{pct(btc.chg)}</span>
            </div>
          </aside>

          <main className="canvas">
            {/* ── HERO ── */}
            <section className="band">
              <div className="inner open-grid">
                <div>
                  <h1 className="claim">Торгуйте криптовалютой <span>просто и наглядно</span></h1>
                  <p className="open-sub">FYNEX — платформа для торговли криптовалютами. Следите за котировками в реальном времени, открывайте счёт и управляйте портфелем в одном интерфейсе.</p>
                  <div className="open-acts">
                    {!isLoggedIn ? (
                      <button className="btn btn-primary" onClick={() => navigate("/SignUp")}>Открыть счёт</button>
                    ) : (
                      <button className="btn btn-primary" onClick={() => navigate(TRADE)}>Перейти к торгам</button>
                    )}
                    <a className="btn btn-line" href="#platform" onClick={go("platform")}>Узнать больше</a>
                  </div>
                  <div className="trust-line">
                    <span>Котировки <b>онлайн</b></span>
                    <span>Регистрация <b>по e-mail</b></span>
                    <span>Портфель <b>в кабинете</b></span>
                  </div>
                </div>
                <Calc assets={assets} isLoggedIn={isLoggedIn} navigate={navigate} />
              </div>
            </section>

            {/* ── BOARD STRIP (live) ── */}
            <section className="band tight">
              <div className="board-strip">
                {assets.map((a) => {
                  const fl = flashes[a.sym];
                  return (
                    <div className="bs-cell" key={a.sym} onClick={() => navigate(`/market?symbol=${a.sym}USDT`)}>
                      <div className="bs-top"><span>{a.sym}/USDT</span><span>{fmtVol(a.qv)}</span></div>
                      <div className="bs-px" style={fl ? { color: fl === "up" ? "var(--green)" : "var(--red)" } : undefined}>{fmt(a.price)}</div>
                      <div className={"bs-chg " + dirOf(a.chg)}>{pct(a.chg)}</div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* ── PLATFORM ── */}
            <Band id="platform" kicker="Платформа"><Depth btc={btc} /></Band>

            {/* ── FEATURES ── */}
            <Band id="features" kicker="Возможности">
              <h2>Основные разделы платформы</h2>
              <Tools />
            </Band>

            {/* ── MARKETS ── */}
            <Band id="markets" kicker="Рынки">
              <h2>Основные рынки</h2>
              <Markets assets={assets} flashes={flashes} navigate={navigate} />
            </Band>

            {/* ── SECURITY ── */}
            <Band id="security" kicker="Безопасность">
              <h2>Простые правила безопасной работы</h2>
              <div className="guards">
                {GUARDS.map(([h, p]) => <div key={h}><h3>{h}</h3><p>{p}</p></div>)}
              </div>
              <div className="audit">
                <span>Никому не сообщайте пароль и коды подтверждения.</span>
                <span>Торговля криптовалютой связана с риском.</span>
              </div>
            </Band>

            {/* ── START ── */}
            <Band id="start" kicker="Начало работы">
              <h2>От регистрации до первой сделки — три шага</h2>
              <div className="onboard">
                {STEPS.map(([n, h, p]) => <div key={n}><span className="no mono">{n}</span><h3>{h}</h3><p>{p}</p></div>)}
              </div>
            </Band>

            {/* ── CLOSING (только для гостей, как и раньше) ── */}
            {!isLoggedIn && (
              <div className="closing">
                <div>
                  <h2>Начните работу на FYNEX</h2>
                  <p>Создайте аккаунт по электронной почте и переходите к рынкам.</p>
                </div>
                <button className="btn btn-primary" style={{ padding: "14px 28px", fontSize: 15 }} onClick={() => navigate("/SignUp")}>Открыть счёт</button>
              </div>
            )}

            {/* ── FOOTER ── */}
            <footer>
              <div className="foot-grid">
                <div className="foot-about">
                  <a className="logo" href="/" onClick={goHome}><span className="logo-mark" /><span className="logo-mark" /><b>FYNEX</b></a>
                  <p>Платформа для торговли криптовалютами: рынки, счёт и портфель в одном интерфейсе.</p>
                  <p className="risk">Торговля криптовалютой связана с риском потери вложенных средств. Оценивайте риск самостоятельно.</p>
                  <p className="risk">Не открывается? → Попробуйте <a className="mirror" href="https://ru.fynex.cc">ru.fynex.cc</a></p>
                </div>
                {FOOT.map(([h, links]) => (
                  <div className="foot-col" key={h}>
                    <h4>{h}</h4>
                    {links.map((x) => (
                      <a key={x.l} href={x.id ? "#" + x.id : x.p} onClick={x.id ? go(x.id) : to(x.p)}>{x.l}</a>
                    ))}
                  </div>
                ))}
              </div>
              <div className="foot-bottom">
                <span>© 2026 FYNEX Exchange</span>
                <span></span>
              </div>
            </footer>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Landing;
